import React, { useEffect, useState, useRef } from 'react';
import { applicantAPI, tagsAPI, filesAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Save, Plus, X, FileDown, Upload, Loader2, Sparkles, Tag, Camera, Trash2, DollarSign } from 'lucide-react';

const CATEGORY_COLORS = {
  technology: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30',
  tools: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
  methodology: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  domain: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  behavioral: 'bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-500/30',
  role: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  other: 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 border-slate-300 dark:border-zinc-700',
};

export default function ApplicantProfile() {
  const { user, loadUser } = useAuthStore();
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const [form, setForm] = useState({
    personal_info: { name: '', email: '', phone: '', location: '', linkedin: '', github: '', behance: '', portfolio_url: '', summary: '', profile_photo_url: '' },
    skills: [],
    education: [],
    work_experience: [],
    industry: '',
    sector: '',
    additional_info: '',
    certifications: [],
    projects: [],
    languages: [],
    visibility_status: 'not_looking',
    profile_public: false,
    auto_approve_setting: 'manual',
    auto_approve_days: 3,
    expected_compensation_min: '',
    expected_compensation_max: '',
    expected_compensation_currency: 'USD',
    notice_period_days: '',
    visible_fields: { personal_info: false, current_company: false, contact_details: false, work_experience: true, education: true, skills: true },
  });
  const [newSkill, setNewSkill] = useState('');
  const [resumeFormat, setResumeFormat] = useState('classic');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeNote, setResumeNote] = useState('');
  const resumeInputRef = useRef(null);

  const loadTags = async () => {
    try { const d = await tagsAPI.getMine(); setTags(d.tags || []); } catch { /* ignore */ }
  };

  useEffect(() => { loadTags(); }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const p = await applicantAPI.getProfile();
        setProfile({ ...p, _is_staffing_managed: !!p.is_staffing_managed });
        setHasProfile(true);
        setForm(prev => ({
          personal_info: p.personal_info || prev.personal_info,
          skills: p.skills || [],
          education: p.education || [],
          work_experience: p.work_experience || [],
          industry: p.industry || '',
          sector: p.sector || '',
          additional_info: p.additional_info || '',
          certifications: p.certifications || [],
          projects: p.projects || [],
          languages: p.languages || [],
          visibility_status: p.visibility_status || 'not_looking',
          profile_public: p.profile_public || false,
          auto_approve_setting: p.auto_approve_setting || 'manual',
          auto_approve_days: p.auto_approve_days || 3,
          expected_compensation_min: p.expected_compensation_min ?? '',
          expected_compensation_max: p.expected_compensation_max ?? '',
          expected_compensation_currency: p.expected_compensation_currency || 'USD',
          notice_period_days: p.notice_period_days ?? '',
          visible_fields: p.visible_fields || prev.visible_fields,
        }));
      } catch { setHasProfile(false); }
    };
    load();
    const onJayUpdate = () => load();
    window.addEventListener('jay:profile-updated', onJayUpdate);
    return () => window.removeEventListener('jay:profile-updated', onJayUpdate);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      // Normalize compensation values: empty string → null
      const payload = {
        ...form,
        expected_compensation_min: form.expected_compensation_min === '' ? null : parseFloat(form.expected_compensation_min),
        expected_compensation_max: form.expected_compensation_max === '' ? null : parseFloat(form.expected_compensation_max),
        notice_period_days: form.notice_period_days === '' ? null : parseInt(form.notice_period_days, 10),
      };
      if (hasProfile) {
        await applicantAPI.updateProfile(payload);
      } else {
        await applicantAPI.createProfile(payload);
        setHasProfile(true);
      }
      setMsg('Profile saved!');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setMsg('Error: photo must be under 5MB'); return; }
    setPhotoUploading(true);
    setMsg('');
    try {
      const r = await filesAPI.uploadProfilePhoto(file);
      setForm(prev => ({ ...prev, personal_info: { ...prev.personal_info, profile_photo_url: r.url } }));
      await loadUser();  // Refresh nav avatar
      setMsg('Profile photo updated');
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('Error: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const removePhoto = async () => {
    setPhotoUploading(true);
    try {
      await filesAPI.deleteProfilePhoto();
      setForm(prev => ({ ...prev, personal_info: { ...prev.personal_info, profile_photo_url: '' } }));
      await loadUser();
    } catch (err) {
      setMsg('Error: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setPhotoUploading(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !form.skills.includes(newSkill.trim())) {
      setForm({ ...form, skills: [...form.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (s) => setForm({ ...form, skills: form.skills.filter(sk => sk !== s) });

  const addTag = async () => {
    const name = tagInput.trim();
    if (!name) return;
    try { const r = await tagsAPI.add(name); setTags(r.tags || tags); setTagInput(''); }
    catch (e) { alert(e?.response?.data?.detail || 'Could not add tag'); }
  };

  const removeTag = async (name) => {
    try { const r = await tagsAPI.remove(name); setTags(r.tags || []); }
    catch (e) { alert(e?.response?.data?.detail || 'Could not remove tag'); }
  };

  const onResumeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeUploading(true);
    try {
      const r = await tagsAPI.uploadResume(file);
      const summary = `${r.action === 'created' ? 'Created' : 'Merged'} profile from resume · ${r.parsed_preview?.skills_count || 0} skills · ${r.parsed_preview?.experience_count || 0} roles · ${(r.tags || []).length} tags`;
      setResumeNote(summary);
      setTags(r.tags || []);
      // Reload profile so Jay's parsed fields show up
      const p = await applicantAPI.getProfile();
      setProfile(p);
      setHasProfile(true);
      setForm(prev => ({
        ...prev,
        personal_info: p.personal_info || prev.personal_info,
        skills: p.skills || [],
        education: p.education || [],
        work_experience: p.work_experience || [],
        industry: p.industry || prev.industry,
        sector: p.sector || prev.sector,
        visibility_status: p.visibility_status || prev.visibility_status,
      }));
    } catch (err) {
      setResumeNote('Error: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setResumeUploading(false);
      if (resumeInputRef.current) resumeInputRef.current.value = '';
    }
  };

  const addEducation = () => setForm({ ...form, education: [...form.education, { degree: '', institution: '', year: '', grade: '' }] });
  const updateEdu = (i, k, v) => { const e = [...form.education]; e[i] = { ...e[i], [k]: v }; setForm({ ...form, education: e }); };
  const removeEdu = (i) => setForm({ ...form, education: form.education.filter((_, idx) => idx !== i) });

  const addExp = () => setForm({ ...form, work_experience: [...form.work_experience, { company: '', title: '', start_date: '', end_date: '', is_current: false, description: '', projects: [] }] });
  const updateExp = (i, k, v) => { const e = [...form.work_experience]; e[i] = { ...e[i], [k]: v }; setForm({ ...form, work_experience: e }); };
  const removeExp = (i) => setForm({ ...form, work_experience: form.work_experience.filter((_, idx) => idx !== i) });

  const downloadResume = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${API_URL}/resume/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ format: resumeFormat })
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.personal_info.name || 'resume'}_resume.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Resume export failed: ' + e.message);
    }
  };

  const pi = form.personal_info;

  return (
    <div data-testid="applicant-profile">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Profile</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">Manage your professional information</p>
        </div>
        <div className="flex gap-2">
          {hasProfile && (
            <div className="flex items-center gap-2">
              <select value={resumeFormat} onChange={e => setResumeFormat(e.target.value)} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-2 py-2 text-xs text-slate-600 dark:text-zinc-300" data-testid="resume-format-select">
                <option value="classic">Classic</option>
                <option value="modern">Modern</option>
                <option value="minimal">Minimal</option>
              </select>
              <button onClick={downloadResume} className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="download-resume-btn"><FileDown className="w-4 h-4" />Export PDF</button>
            </div>
          )}
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm hover:bg-slate-800 dark:hover:bg-zinc-200 disabled:opacity-50" data-testid="save-profile-btn"><Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      {msg && <div className={`px-4 py-2 rounded-lg mb-4 text-sm ${msg.startsWith('Error') ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>{msg}</div>}

      <div className="space-y-6">
        {/* Resume Auto-Fill */}
        <div className="bg-gradient-to-br from-emerald-500/10 via-white dark:via-[#111114] to-white dark:to-[#111114] border border-emerald-500/30 rounded-2xl p-5" data-testid="resume-upload-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                <span className="text-xs uppercase tracking-widest text-emerald-300/80">Jay autofill</span>
              </div>
              <h2 className="text-base text-slate-900 dark:text-white font-medium">Upload your resume — Jay fills the rest</h2>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">PDF, DOCX, or TXT (max 10 MB). Jay parses your experience, skills, and tags, then merges them into your profile. Your existing data is preserved.</p>
            </div>
            <div>
              <input ref={resumeInputRef} type="file" accept=".pdf,.docx,.txt,application/pdf" className="hidden" onChange={onResumeFile} data-testid="resume-file-input" />
              <button onClick={() => resumeInputRef.current?.click()} disabled={resumeUploading} data-testid="upload-resume-btn"
                className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                {resumeUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> Upload resume</>}
              </button>
            </div>
          </div>
          {resumeNote && <p className={`text-xs mt-3 ${resumeNote.startsWith('Error') ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`} data-testid="resume-upload-note">{resumeNote}</p>}
        </div>

        {/* Tags */}
        <Section title="Tags" action={<span className="text-[11px] text-slate-500 dark:text-zinc-500">Auto-detected · editable</span>}>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-3 flex items-center gap-1.5"><Tag className="w-3 h-3" /> These tags surface your profile to matching jobs and power your filtered skill cloud.</p>
          <div className="flex gap-2 mb-3">
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add a tag (e.g. Leadership, Kubernetes)…" className="flex-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="tag-input" />
            <button onClick={addTag} className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="add-tag-btn"><Plus className="w-4 h-4" /></button>
          </div>
          {tags.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-zinc-600">No tags yet — upload a resume or add tags above.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="tags-list">
              {tags.map(t => (
                <span key={t.name} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${CATEGORY_COLORS[t.category] || CATEGORY_COLORS.other}`} data-testid={`tag-${t.name}`}>
                  <span>{t.name}</span>
                  <span className="text-[9px] uppercase tracking-wider opacity-70">{t.category}</span>
                  <button onClick={() => removeTag(t.name)} className="ml-0.5 opacity-70 hover:opacity-100" data-testid={`remove-tag-${t.name}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </Section>

        {/* Personal Info */}
        <Section title="Personal Information">
          {/* Profile Photo */}
          <div className="flex items-center gap-5 pb-5 mb-5 border-b border-slate-200 dark:border-zinc-800/60" data-testid="profile-photo-section">
            <div className="relative">
              {form.personal_info.profile_photo_url ? (
                <img
                  src={filesAPI.getFileUrl(form.personal_info.profile_photo_url)}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-2 border-slate-300 dark:border-zinc-700"
                  data-testid="profile-photo-img"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-zinc-800 border-2 border-slate-300 dark:border-zinc-700 flex items-center justify-center text-2xl font-medium text-slate-500 dark:text-zinc-400" data-testid="profile-photo-placeholder">
                  {(form.personal_info.name || user?.email || '?').charAt(0).toUpperCase()}
                </div>
              )}
              {photoUploading && (
                <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-slate-900 dark:text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-900 dark:text-white font-medium mb-0.5">Profile photo</p>
              <p className="text-xs text-slate-500 dark:text-zinc-500 mb-3">Square JPG/PNG/WEBP, under 5MB. Shows on your matches and applications.</p>
              <div className="flex items-center gap-2">
                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onPhotoFile} data-testid="profile-photo-input" />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={photoUploading}
                  data-testid="upload-photo-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-200 rounded-lg text-xs disabled:opacity-50"
                >
                  <Camera className="w-3.5 h-3.5" /> {form.personal_info.profile_photo_url ? 'Replace' : 'Upload'}
                </button>
                {form.personal_info.profile_photo_url && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    disabled={photoUploading}
                    data-testid="remove-photo-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-transparent hover:bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg text-xs border border-red-500/20"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Full Name" value={pi.name} onChange={v => setForm({...form, personal_info: {...pi, name: v}})} />
            <Input
              label="Email (login)"
              value={user?.email || pi.email}
              onChange={() => {}}
              type="email"
              disabled
              hint="Your login email — cannot be changed. Contact support if you need to update it."
            />
            <Input
              label="Phone"
              value={user?.phone || pi.phone}
              onChange={() => {}}
              disabled
              hint={profile?._is_staffing_managed ? 'Managed by your staffing organization' : 'Verified via WhatsApp. Change it from Settings.'}
            />
            <Input label="Location" value={pi.location} onChange={v => setForm({...form, personal_info: {...pi, location: v}})} />
            <Input label="LinkedIn" value={pi.linkedin} onChange={v => setForm({...form, personal_info: {...pi, linkedin: v}})} />
            <Input label="GitHub" value={pi.github} onChange={v => setForm({...form, personal_info: {...pi, github: v}})} />
            <Input label="Behance" value={pi.behance || ''} onChange={v => setForm({...form, personal_info: {...pi, behance: v}})} placeholder="https://behance.net/..." />
            <Input label="Portfolio URL" value={pi.portfolio_url || ''} onChange={v => setForm({...form, personal_info: {...pi, portfolio_url: v}})} placeholder="https://..." />
          </div>
          <div className="mt-4">
            <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Professional Summary</label>
            <textarea value={pi.summary || ''} onChange={e => setForm({...form, personal_info: {...pi, summary: e.target.value}})} rows={3} placeholder="Brief professional summary..." className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="summary-input" />
          </div>
        </Section>

        {/* Stand-out section */}
        <Section title="What Sets You Apart">
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-2">Share something interesting that boosts your candidature — hobbies, achievements, side projects, volunteer work, etc.</p>
          <textarea value={form.additional_info || ''} onChange={e => setForm({...form, additional_info: e.target.value})} rows={3} placeholder="e.g. Built an open-source tool with 500+ stars, mentor at Code.org, competitive programmer..." className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="additional-info-input" />
        </Section>

        {/* Visibility */}
        <Section title="Visibility & Industry">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Status</label>
              <select value={form.visibility_status} onChange={e => setForm({...form, visibility_status: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white">
                <option value="actively_looking">Actively Looking</option>
                <option value="exploring">Exploring</option>
                <option value="not_looking">Not Looking</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-slate-500 dark:text-zinc-400">Public Profile</label>
              <button onClick={() => setForm({...form, profile_public: !form.profile_public})} className={`w-10 h-5 rounded-full transition-colors ${form.profile_public ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-zinc-700'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.profile_public ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Industry</label>
              <select value={form.industry || ''} onChange={e => setForm({...form, industry: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="industry-select">
                <option value="">Select Industry</option>
                {["Information Technology","FinTech","HealthTech","EdTech","E-Commerce","Manufacturing","Banking & Finance","Consulting","Media & Entertainment","Logistics","Government","Retail","Telecom","Real Estate","Other"].map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <Input label="Sector" value={form.sector || ''} onChange={v => setForm({...form, sector: v})} placeholder="e.g. SaaS, B2B, Enterprise" />
          </div>
        </Section>

        {/* Expected Compensation — hidden for staffing-managed candidates */}
        {!profile?._is_staffing_managed && (
        <Section title="Expected Compensation" action={<span className="text-[11px] text-slate-500 dark:text-zinc-500">Used for smart job matching</span>}>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-3 flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> Jobs with no posted compensation match you regardless. Leave blank if you&apos;re flexible.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Minimum</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={form.expected_compensation_min}
                onChange={e => setForm({...form, expected_compensation_min: e.target.value})}
                placeholder="e.g. 60000"
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
                data-testid="expected-comp-min-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Maximum</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={form.expected_compensation_max}
                onChange={e => setForm({...form, expected_compensation_max: e.target.value})}
                placeholder="e.g. 90000"
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
                data-testid="expected-comp-max-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Currency</label>
              <select
                value={form.expected_compensation_currency}
                onChange={e => setForm({...form, expected_compensation_currency: e.target.value})}
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                data-testid="expected-comp-currency-select"
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="AUD">AUD</option>
                <option value="SGD">SGD</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          </div>
        </Section>
        )}

        {/* Notice Period — shown to all candidates (incl. staffing-managed) */}
        <Section title="Notice Period" action={<span className="text-[11px] text-slate-500 dark:text-zinc-500">Used for smart job matching</span>}>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-3">
            Days you need to serve before joining a new role. If you&apos;re currently serving notice, enter the remaining days. Leave blank if unknown — you&apos;ll be matched to all postings regardless.
          </p>
          <div className="max-w-xs">
            <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Notice period (days)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.notice_period_days}
              onChange={e => setForm({...form, notice_period_days: e.target.value})}
              placeholder="e.g. 30 · 0 = immediate joiner"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
              data-testid="notice-period-input"
            />
          </div>
        </Section>

        {/* Skills */}
        <Section title="Skills">
          <div className="flex gap-2 mb-3">
            <input value={newSkill} onChange={e => setNewSkill(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())} placeholder="Add a skill" className="flex-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="skill-input" />
            <button onClick={addSkill} className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.skills.map(s => (
              <span key={s} className="flex items-center gap-1 px-3 py-1 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-full text-xs">
                {s} <button onClick={() => removeSkill(s)}><X className="w-3 h-3 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400" /></button>
              </span>
            ))}
          </div>
        </Section>

        {/* Work Experience */}
        <Section title="Work Experience" action={<button onClick={addExp} className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"><Plus className="w-4 h-4 inline" /> Add</button>}>
          {form.work_experience.map((exp, i) => (
            <div key={i} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 mb-3 relative">
              <button onClick={() => removeExp(i)} className="absolute top-2 right-2 text-slate-500 dark:text-zinc-600 hover:text-red-600 dark:hover:text-red-400"><X className="w-4 h-4" /></button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Title" value={exp.title} onChange={v => updateExp(i, 'title', v)} />
                <Input label="Company" value={exp.company} onChange={v => updateExp(i, 'company', v)} />
                <Input label="Start Date" value={exp.start_date} onChange={v => updateExp(i, 'start_date', v)} placeholder="YYYY-MM" />
                <Input label="End Date" value={exp.end_date || ''} onChange={v => updateExp(i, 'end_date', v)} placeholder="YYYY-MM" />
              </div>
              <div className="mt-3">
                <Input label="Description" value={exp.description || ''} onChange={v => updateExp(i, 'description', v)} textarea />
              </div>
            </div>
          ))}
        </Section>

        {/* Education */}
        <Section title="Education" action={<button onClick={addEducation} className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"><Plus className="w-4 h-4 inline" /> Add</button>}>
          {form.education.map((edu, i) => (
            <div key={i} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 mb-3 relative">
              <button onClick={() => removeEdu(i)} className="absolute top-2 right-2 text-slate-500 dark:text-zinc-600 hover:text-red-600 dark:hover:text-red-400"><X className="w-4 h-4" /></button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Degree" value={edu.degree} onChange={v => updateEdu(i, 'degree', v)} />
                <Input label="Institution" value={edu.institution} onChange={v => updateEdu(i, 'institution', v)} />
                <Input label="Year" value={edu.year} onChange={v => updateEdu(i, 'year', v)} />
                <Input label="Grade" value={edu.grade || ''} onChange={v => updateEdu(i, 'grade', v)} />
              </div>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-slate-900 dark:text-white font-medium text-sm">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, textarea, disabled = false, hint }) {
  const cls = `w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`;
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">{label}</label>
      {textarea
        ? <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className={cls} disabled={disabled} />
        : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={cls} disabled={disabled} />
      }
      {hint && <p className="text-[11px] text-slate-500 dark:text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}
